import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { useAuth } from '../../contexts/AuthContext';
import { jobAPI } from '../../services/api';
import Header from '../Header';
import Footer from '../Footer';
import BottomNavigationBar from '../BottomNavigationBar';
import JobDetailPanel from './JobDetailPanel';
import JobPostingForm from './JobPostingForm';
import RecruiterTermsModal from '../Company/RecruiterTermsModal';
import './MyJobsPage.css';

const MyJobsPage = ({ 
  onHomeClick, 
  onCategoriesClick, 
  onHowItWorksClick, 
  onProfileClick, 
  onAccountSettingsClick, 
  onForumClick 
}) => {
  const { user, isAuthenticated, switchMode } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applications, setApplications] = useState({});
  const [activeFilter, setActiveFilter] = useState('all'); // 'all', 'open', 'closed', 'deleted'
  const [showJobPostingForm, setShowJobPostingForm] = useState(false);
  const [jobToEdit, setJobToEdit] = useState(null);
  const [showMobileModal, setShowMobileModal] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [fetchingApplications, setFetchingApplications] = useState(new Set()); // Track which jobs are being fetched
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [isSwitchingMode, setIsSwitchingMode] = useState(false);
  const lastSelectedJobIdRef = useRef(null); // Track last selected job to prevent duplicate clicks
  const isSelectingRef = useRef(false); // Track if we're currently selecting a job
  // Server-side pagination and search
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalJobs, setTotalJobs] = useState(0);
  const [titleSearch, setTitleSearch] = useState('');
  const [hasMoreJobs, setHasMoreJobs] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  // Initial load and filter changes (no debounce)
  useEffect(() => {
    if (!user) return;
    fetchMyJobs(true); // Reset jobs when user, filter changes
  }, [user, activeFilter]);

  // Debounced search effect (only for titleSearch)
  useEffect(() => {
    if (!user) return;
    
    // Debounce the search to avoid too many requests
    const debounceTimer = setTimeout(() => {
      fetchMyJobs(true); // Reset jobs when search changes
    }, 500); // Wait 500ms after user stops typing

    return () => clearTimeout(debounceTimer);
  }, [titleSearch]);


  // Detect mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 1024);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Handle body scroll when modal is open
  useEffect(() => {
    if (showMobileModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [showMobileModal]);

  // Handle jobId or job from URL when jobs are already loaded (backup for when jobs load before URL param is processed)
  useEffect(() => {
    const jobIdFromUrl = searchParams.get('jobId') || searchParams.get('job');
    const tabFromUrl = searchParams.get('tab');
    if (jobIdFromUrl && jobs.length > 0) {
      const jobToSelect = jobs.find(job => {
        const jobId = job.id.toString();
        const urlJobId = jobIdFromUrl.toString();
        // Check by ID (numeric) or by share_token (for secure access)
        return jobId === urlJobId || 
               job.id === parseInt(jobIdFromUrl) || 
               (job.share_token && job.share_token === jobIdFromUrl);
      });
      
      if (jobToSelect && (!selectedJob || 
          (selectedJob.id.toString() !== jobIdFromUrl && selectedJob.share_token !== jobIdFromUrl))) {
        setSelectedJob(jobToSelect);
        fetchApplicationsForJob(jobToSelect.id);
        // Store tab parameter for JobDetailPanel
        if (tabFromUrl) {
          sessionStorage.setItem(`job-${jobToSelect.id}-initialTab`, tabFromUrl);
        }
        // Remove the query parameters from URL after selecting
        setSearchParams({}, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, jobs.length]);

  const fetchApplicationsForJob = async (jobId) => {
    // Prevent duplicate API calls for the same job
    if (fetchingApplications.has(jobId)) {
      console.log(`[MyJobsPage] Already fetching applications for job ${jobId}, skipping API call...`);
      return; // Already fetching
    }
    
    // Check if we already have applications for this job
    if (applications[jobId] !== undefined) {
      console.log(`[MyJobsPage] Applications already cached for job ${jobId}, skipping API call...`);
      return; // Already fetched
    }

    console.log(`[MyJobsPage] Fetching applications for job ${jobId}...`);
    setFetchingApplications(prev => new Set(prev).add(jobId));
    
    try {
      const response = await jobAPI.getApplications(jobId);
      if (response.success) {
        const apps = response.data?.data || response.data || [];
        setApplications(prev => ({
          ...prev,
          [jobId]: apps
        }));
        console.log(`[MyJobsPage] Successfully fetched ${apps.length} applications for job ${jobId}`);
      }
    } catch (err) {
      console.error(`[MyJobsPage] Error fetching applications for job ${jobId}:`, err);
      setApplications(prev => ({
        ...prev,
        [jobId]: []
      }));
    } finally {
      setFetchingApplications(prev => {
        const newSet = new Set(prev);
        newSet.delete(jobId);
        return newSet;
      });
    }
  };

  const fetchMyJobs = async (reset = false, preserveSelection = false) => {
    if (!user) return;
    
    if (reset) {
      setLoading(true);
      setCurrentPage(1);
    } else {
      setLoadingMore(true);
    }
    
    try {
      const pageToFetch = reset ? 1 : currentPage + 1;
      
      // Build query parameters for server-side pagination and filtering
      const params = {
        posted_by: user.acting_user_id ?? user.id,
        include_deleted: 'true',
        per_page: perPage,
        page: pageToFetch,
      };

      // Add title search if provided
      if (titleSearch.trim()) {
        params.title = titleSearch.trim();
      }

      // Add status filter for server-side filtering
      if (activeFilter === 'open') {
        params.status = 'published';
        params.is_active = '1';
      } else if (activeFilter === 'closed') {
        params.status = 'closed';
      } else if (activeFilter === 'deleted') {
        // For deleted, we still need to fetch with include_deleted and filter client-side
        // since Laravel pagination doesn't handle soft deletes well
      }
      
      const response = await jobAPI.getAll(params);
      
      if (response.success) {
        // Handle paginated response
        const paginationData = response.data;
        let jobsData = [];
        
        if (paginationData?.data && Array.isArray(paginationData.data)) {
          jobsData = paginationData.data;
          // Set pagination info
          const lastPage = paginationData.last_page || 1;
          setTotalPages(lastPage);
          setTotalJobs(paginationData.total || 0);
          setHasMoreJobs(pageToFetch < lastPage);
        } else if (Array.isArray(paginationData)) {
          jobsData = paginationData;
          setHasMoreJobs(false);
        }
        
        const previousSelectedJobId = preserveSelection && selectedJob ? selectedJob.id : null;
        
        // For deleted filter, we need to filter client-side since soft deletes
        let filteredJobs = jobsData;
        if (activeFilter === 'deleted') {
          filteredJobs = jobsData.filter(job => job.deleted_at);
        }
        
        // Append or replace jobs based on reset flag
        if (reset) {
          setJobs(filteredJobs);
          setCurrentPage(1);
        } else {
          setJobs(prev => [...prev, ...filteredJobs]);
          setCurrentPage(pageToFetch);
        }
        
        // Check if there's a jobId in the URL query params
        const jobIdFromUrl = searchParams.get('jobId');
        const allJobs = reset ? filteredJobs : [...jobs, ...filteredJobs];
        
        if (jobIdFromUrl) {
          const jobToSelect = allJobs.find(job => job.id === parseInt(jobIdFromUrl) || job.id.toString() === jobIdFromUrl);
          if (jobToSelect) {
            setSelectedJob(jobToSelect);
            // Fetch applications for the selected job
            fetchApplicationsForJob(jobToSelect.id);
            // Remove the query parameter from URL after selecting
            setSearchParams({});
          } else {
            // If job not found, select first job by default
            if (allJobs.length > 0 && !selectedJob) {
              setSelectedJob(allJobs[0]);
              fetchApplicationsForJob(allJobs[0].id);
            } else if (allJobs.length === 0) {
              setSelectedJob(null);
            }
          }
        } else if (preserveSelection && previousSelectedJobId) {
          // Try to preserve the previously selected job
          const stillExists = allJobs.find(job => job.id === previousSelectedJobId);
          if (stillExists) {
            setSelectedJob(stillExists);
            fetchApplicationsForJob(stillExists.id);
          } else {
            // Selected job was deleted or filtered out, select first available or clear
            if (allJobs.length > 0 && !selectedJob) {
              setSelectedJob(allJobs[0]);
              fetchApplicationsForJob(allJobs[0].id);
            } else if (allJobs.length === 0) {
              setSelectedJob(null);
            }
          }
        } else {
          // Select first job by default if available and no jobId in URL
          if (allJobs.length > 0 && !selectedJob && reset) {
            setSelectedJob(allJobs[0]);
            fetchApplicationsForJob(allJobs[0].id);
          } else if (allJobs.length === 0) {
            setSelectedJob(null);
          }
        }
      }
    } catch (err) {
      console.error('Error fetching my jobs:', err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleLoadMore = () => {
    if (!loadingMore && hasMoreJobs) {
      fetchMyJobs(false, true);
    }
  };

  // Desktop handler - only for desktop
  const handleDesktopJobSelect = async (job, event) => {
    // Check if Ctrl (Windows/Linux) or Cmd (Mac) is pressed
    const isModifierPressed = event?.ctrlKey || event?.metaKey;
    
    if (isModifierPressed) {
      // Open job detail page in new tab
      event?.preventDefault();
      event?.stopPropagation();
      // Use share_token if available, otherwise use ID
      const jobUrl = job.share_token 
        ? `/job/${job.share_token}`
        : `/my-jobs?jobId=${job.id}`;
      window.open(jobUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    // Prevent event propagation and default behavior
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    // Prevent duplicate clicks on the same job
    if (lastSelectedJobIdRef.current === job.id && isSelectingRef.current) {
      console.log(`[MyJobsPage Desktop] Duplicate click detected for job ${job.id}, ignoring...`);
      return; // Already processing this job
    }

    // Prevent selecting the same job if already selected
    if (selectedJob?.id === job.id) {
      console.log(`[MyJobsPage Desktop] Job ${job.id} already selected, skipping...`);
      return; // Already selected
    }

    // Mark that we're selecting a job
    isSelectingRef.current = true;
    lastSelectedJobIdRef.current = job.id;

    console.log(`[MyJobsPage Desktop] Selecting job ${job.id}...`);

    try {
      setSelectedJob(job);
      // Fetch applications for this job (function will prevent duplicate calls)
      fetchApplicationsForJob(job.id);
    } finally {
      // Reset the flag after a short delay to allow state updates
      setTimeout(() => {
        isSelectingRef.current = false;
      }, 100);
    }
  };

  // Mobile handler - only for mobile
  const handleMobileJobSelect = async (job, event) => {
    // Check if Ctrl (Windows/Linux) or Cmd (Mac) is pressed
    const isModifierPressed = event?.ctrlKey || event?.metaKey;
    
    if (isModifierPressed) {
      // Open job detail page in new tab
      event?.preventDefault();
      event?.stopPropagation();
      // Use share_token if available, otherwise use ID
      const jobUrl = job.share_token 
        ? `/job/${job.share_token}`
        : `/my-jobs?jobId=${job.id}`;
      window.open(jobUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    // Prevent event propagation and default behavior
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    // Prevent duplicate clicks on the same job
    if (lastSelectedJobIdRef.current === job.id && isSelectingRef.current) {
      console.log(`[MyJobsPage Mobile] Duplicate click detected for job ${job.id}, ignoring...`);
      return; // Already processing this job
    }

    // Prevent selecting the same job if already selected
    if (selectedJob?.id === job.id) {
      // If modal is not open, open it
      if (!showMobileModal) {
        setShowMobileModal(true);
      }
      console.log(`[MyJobsPage Mobile] Job ${job.id} already selected, skipping...`);
      return; // Already selected
    }

    // Mark that we're selecting a job
    isSelectingRef.current = true;
    lastSelectedJobIdRef.current = job.id;

    console.log(`[MyJobsPage Mobile] Selecting job ${job.id}...`);

    try {
      setSelectedJob(job);
      // Fetch applications for this job (function will prevent duplicate calls)
      fetchApplicationsForJob(job.id);
      // Show modal on mobile
      setShowMobileModal(true);
    } finally {
      // Reset the flag after a short delay to allow state updates
      setTimeout(() => {
        isSelectingRef.current = false;
      }, 100);
    }
  };

  const handlePostNewJobClick = () => {
    // Reset job to edit when posting new job
    setJobToEdit(null);
    
    // Check if user is in recruiter mode
    const currentMode = user?.active_mode || user?.default_mode || 'personal';
    const isCompanyMode = currentMode === 'company';
    const termsAccepted = user?.job_posting_terms_accepted || false;

    // If not in recruiter mode, check terms first
    if (!isCompanyMode) {
      // If terms not accepted, show terms modal first
      if (!termsAccepted) {
        setShowTermsModal(true);
        return;
      }
      // Terms accepted but not in recruiter mode, switch mode first
      switchToRecruiterModeAndOpenForm();
      return;
    }

    // Already in recruiter mode, open form directly
    setShowJobPostingForm(true);
  };

  const handleEditJob = (job) => {
    // Set the job to edit and open the form
    setJobToEdit(job);
    setShowJobPostingForm(true);
  };

  const switchToRecruiterModeAndOpenForm = async () => {
    setIsSwitchingMode(true);
    try {
      const result = await switchMode('company', false, true);
      if (result.success) {
        setShowJobPostingForm(true);
      } else {
        alert(result.message || 'Failed to switch to recruiter mode');
      }
    } catch (error) {
      alert(error.message || 'Failed to switch to recruiter mode');
    } finally {
      setIsSwitchingMode(false);
    }
  };

  const handleTermsAccept = async () => {
    setShowTermsModal(false);
    setIsSwitchingMode(true);
    try {
      const result = await switchMode('company', true, true);
      if (result.success) {
        setShowJobPostingForm(true);
      } else {
        alert(result.message || 'Failed to switch to recruiter mode');
      }
    } catch (error) {
      alert(error.message || 'Failed to switch to recruiter mode');
    } finally {
      setIsSwitchingMode(false);
    }
  };

  const handleFormClose = () => {
    setShowJobPostingForm(false);
    setJobToEdit(null);
  };

  const handleFormSuccess = (job) => {
    console.log('Job posted/updated successfully:', job);
    setShowJobPostingForm(false);
    
    // If we were editing a job, update the selected job immediately with fresh data
    if (jobToEdit && job && job.id === jobToEdit.id) {
      // Update selected job with the fresh data from API
      setSelectedJob(job);
      // Refresh applications for the updated job
      fetchApplicationsForJob(job.id);
      // Also update the job in the jobs list
      setJobs(prev => prev.map(j => j.id === job.id ? job : j));
    }
    
    // Clear edit state
    setJobToEdit(null);
    
    // Refresh the jobs list (this will preserve selection if job still exists)
    fetchMyJobs(true, true);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
    return `${Math.floor(diffDays / 365)} years ago`;
  };

  const formatSalary = (job) => {
    if (!job.salary_min && !job.salary_max) {
      return null;
    }
    
    const currency = job.salary_currency || 'USD';
    const symbol = currency === 'USD' ? '$' : currency === 'EUR' ? '€' : currency === 'GBP' ? '£' : currency;
    const period = job.salary_period === 'yearly' ? 'year' : 
                   job.salary_period === 'monthly' ? 'month' :
                   job.salary_period === 'hourly' ? 'hour' : job.salary_period;
    
    if (job.salary_min && job.salary_max) {
      return `${symbol}${job.salary_min.toLocaleString()} - ${symbol}${job.salary_max.toLocaleString()}/${period}`;
    } else if (job.salary_min) {
      return `${symbol}${job.salary_min.toLocaleString()}+/${period}`;
    }
    return null;
  };

  return (
    <div className="my-jobs-page my-jobs-with-bottom-nav">
      {/* Header - visible on desktop/tablet, hidden on mobile */}
      <div className="my-jobs-header-nav">
        <Header 
          onHomeClick={onHomeClick}
          onCategoriesClick={onCategoriesClick}
          onHowItWorksClick={onHowItWorksClick}
          onProfileClick={onProfileClick}
          onAccountSettingsClick={onAccountSettingsClick}
          onForumClick={onForumClick}
          onJobsClick={() => navigate('/vacancies')}
          isLoggedIn={isAuthenticated}
          user={user}
          currentPage="my-jobs"
        />
      </div>
      
      <div className="my-jobs-container">
        {/* Desktop Layout - Only render when NOT on mobile */}
        {!isMobile && (
          <div className="my-jobs-layout">
            {/* Left Sidebar - Job List */}
            <div className="my-jobs-sidebar">
            <div className="my-jobs-header">
              <div className="my-jobs-header-top">
                <h2>My Jobs</h2>
                <button 
                  className="new-job-btn"
                  onClick={handlePostNewJobClick}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="5" x2="12" y2="19"></line>
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                  </svg>
                  Post New Job
                </button>
              </div>
              <div className="my-jobs-navigation-tabs">
                <button
                  className={`nav-tab ${location.pathname === '/my-jobs' ? 'active' : ''}`}
                  onClick={() => navigate('/my-jobs')}
                >
                  Posted Jobs
                </button>
                <button
                  className={`nav-tab ${location.pathname === '/applied-jobs' ? 'active' : ''}`}
                  onClick={() => navigate('/applied-jobs')}
                >
                  Applied Jobs
                </button>
              </div>
            </div>

            {/* Search Section */}
            <div className="my-jobs-search-section">
              <div className="my-jobs-search-input-wrapper">
                <svg 
                  className="my-jobs-search-icon" 
                  width="16" 
                  height="16" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                >
                  <circle cx="11" cy="11" r="8"></circle>
                  <path d="m21 21-4.35-4.35"></path>
                </svg>
                <input
                  type="text"
                  className="my-jobs-search-input"
                  placeholder="Search by job title..."
                  value={titleSearch}
                  onChange={(e) => {
                    setTitleSearch(e.target.value);
                  }}
                />
                {titleSearch && (
                  <button
                    className="my-jobs-search-clear"
                    onClick={() => {
                      setTitleSearch('');
                    }}
                    title="Clear search"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </button>
                )}
              </div>
            </div>

            {/* Filter Tabs */}
            <div className="my-jobs-filters">
              <button
                className={`filter-tab ${activeFilter === 'all' ? 'active' : ''}`}
                onClick={() => setActiveFilter('all')}
              >
                All
              </button>
              <button
                className={`filter-tab ${activeFilter === 'open' ? 'active' : ''}`}
                onClick={() => setActiveFilter('open')}
              >
                Open
              </button>
              <button
                className={`filter-tab ${activeFilter === 'closed' ? 'active' : ''}`}
                onClick={() => setActiveFilter('closed')}
              >
                Completed
              </button>
              <button
                className={`filter-tab ${activeFilter === 'deleted' ? 'active' : ''}`}
                onClick={() => setActiveFilter('deleted')}
              >
                Deleted
              </button>
            </div>

            {loading ? (
              <div className="my-jobs-loading">
                <div className="my-jobs-spinner"></div>
                <span>Loading jobs...</span>
              </div>
            ) : jobs.length === 0 ? (
              <div className="my-jobs-empty">
                <div className="my-jobs-empty-icon">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
                    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
                  </svg>
                </div>
                <h3>No jobs posted yet</h3>
                <p>Start posting jobs to see them here</p>
                <button 
                  className="empty-post-job-btn"
                  onClick={handlePostNewJobClick}
                >
                  Post Your First Job
                </button>
              </div>
            ) : (
              <>
                <div className="my-jobs-list">
                  {jobs.map((job) => (
                    <div
                      key={job.id}
                      className={`my-job-item ${selectedJob?.id === job.id ? 'active' : ''}`}
                      onClick={(e) => handleDesktopJobSelect(job, e)}
                    >
                      <div className="my-job-item-header">
                        <h3 className="my-job-item-title">{job.title}</h3>
                        {job.deleted_at ? (
                          <span className="my-job-status-badge deleted">Deleted</span>
                        ) : job.status === 'closed' ? (
                          <span className="my-job-status-badge closed">Completed</span>
                        ) : job.status === 'draft' ? (
                          <span className="my-job-status-badge draft">Draft</span>
                        ) : job.is_active && job.status === 'published' ? (
                          <span className="my-job-status-badge active">Active</span>
                        ) : (
                          <span className="my-job-status-badge inactive">Inactive</span>
                        )}
                      </div>
                      <div className="my-job-item-meta">
                        <span className="my-job-company">{job.company?.name || 'Your Company'}</span>
                        {job.work_type && (
                          <>
                            <span className="my-job-separator">•</span>
                            <span className="my-job-type">{job.work_type}</span>
                          </>
                        )}
                      </div>
                      {job.application_count > 0 && (
                        <div className="my-job-applications">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                            <circle cx="9" cy="7" r="4"></circle>
                            <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                            <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                          </svg>
                          {job.application_count} {job.application_count === 1 ? 'application' : 'applications'}
                        </div>
                      )}
                      <div className="my-job-item-footer">
                        {formatSalary(job) && (
                          <span className="my-job-salary">{formatSalary(job)}</span>
                        )}
                        <span className="my-job-date">{formatDate(job.created_at)}</span>
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Load More Button */}
                {hasMoreJobs && (
                  <div className="my-jobs-load-more">
                    <button
                      className="load-more-btn"
                      onClick={handleLoadMore}
                      disabled={loadingMore || loading}
                    >
                      {loadingMore ? (
                        <>
                          <div className="load-more-spinner"></div>
                          Loading...
                        </>
                      ) : (
                        'Load More'
                      )}
                    </button>
                    {totalJobs > 0 && (
                      <div className="load-more-info">
                        Showing {jobs.length} of {totalJobs} jobs
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
            </div>

               {/* Right Panel - Job Details */}
               <div className="my-jobs-detail-panel">
                 {selectedJob ? (
                   <JobDetailPanel 
                     job={selectedJob} 
                     applications={applications[selectedJob.id] || []}
                     onJobUpdate={() => {
                       fetchMyJobs(true, true); // Reset and preserve selection if job still exists
                     }}
                     onEdit={handleEditJob}
                   />
                 ) : (
              <div className="my-jobs-no-selection">
                <div className="no-selection-icon">
                  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
                    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
                  </svg>
                </div>
                <h3>Select a job to view details</h3>
                <p>Choose a job from the list to see its details and applications</p>
              </div>
            )}
          </div>
        </div>
        )}

      {/* Mobile: Jobs list - Only render when on mobile */}
      {isMobile && (
        <div className="my-jobs-mobile-content">
          <div className="my-jobs-mobile-header">
            <h1>My Jobs</h1>
            {/* Navigation Tabs - Mobile */}
            <div className="my-jobs-navigation-tabs-mobile">
              <button
                className={`nav-tab-mobile ${location.pathname === '/my-jobs' ? 'active' : ''}`}
                onClick={() => navigate('/my-jobs')}
              >
                Posted Jobs
              </button>
              <button
                className={`nav-tab-mobile ${location.pathname === '/applied-jobs' ? 'active' : ''}`}
                onClick={() => navigate('/applied-jobs')}
              >
                Applied Jobs
              </button>
            </div>
            {/* Search Section - Mobile */}
            <div className="my-jobs-search-section-mobile">
              <div className="my-jobs-search-input-wrapper">
                <svg className="my-jobs-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"></circle>
                  <path d="m21 21-4.35-4.35"></path>
                </svg>
                <input
                  type="text"
                  className="my-jobs-search-input"
                  placeholder="Search by job title..."
                  value={titleSearch}
                  onChange={(e) => {
                    setTitleSearch(e.target.value);
                  }}
                />
                {titleSearch && (
                  <button className="my-jobs-search-clear" onClick={() => {
                    setTitleSearch('');
                  }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </button>
                )}
              </div>
            </div>
            {/* Filter Tabs - Mobile */}
            <div className="my-jobs-filters-mobile">
              <button
                className={`filter-tab-mobile ${activeFilter === 'all' ? 'active' : ''}`}
                onClick={() => setActiveFilter('all')}
              >
                All
              </button>
              <button
                className={`filter-tab-mobile ${activeFilter === 'open' ? 'active' : ''}`}
                onClick={() => setActiveFilter('open')}
              >
                Open
              </button>
              <button
                className={`filter-tab-mobile ${activeFilter === 'closed' ? 'active' : ''}`}
                onClick={() => setActiveFilter('closed')}
              >
                Completed
              </button>
              <button
                className={`filter-tab-mobile ${activeFilter === 'deleted' ? 'active' : ''}`}
                onClick={() => setActiveFilter('deleted')}
              >
                Deleted
              </button>
            </div>
          </div>

          <div className="my-jobs-mobile-list">
            {loading ? (
              <div className="loading-state">Loading jobs...</div>
            ) : jobs.length === 0 ? (
              <div className="empty-state">
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
                  <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
                </svg>
                <h3>No jobs posted yet</h3>
                <p>Start posting jobs to see them here</p>
                <button 
                  className="browse-jobs-btn"
                  onClick={handlePostNewJobClick}
                >
                  Post Your First Job
                </button>
              </div>
            ) : (
              <>
                {jobs.map((job) => (
                  <div
                    key={job.id}
                    className="my-job-item-mobile"
                    onClick={(e) => handleMobileJobSelect(job, e)}
                  >
                    <div className="my-job-item-header">
                      <h3 className="my-job-item-title">{job.title}</h3>
                      {job.deleted_at ? (
                        <span className="my-job-status-badge deleted">Deleted</span>
                      ) : job.status === 'closed' ? (
                        <span className="my-job-status-badge closed">Completed</span>
                      ) : job.is_active && job.status === 'published' ? (
                        <span className="my-job-status-badge active">Active</span>
                      ) : (
                        <span className="my-job-status-badge inactive">Inactive</span>
                      )}
                    </div>
                    <div className="my-job-item-meta">
                      <span className="my-job-company">{job.company?.name || 'Your Company'}</span>
                      {job.work_type && (
                        <>
                          <span className="my-job-separator">•</span>
                          <span className="my-job-type">{job.work_type}</span>
                        </>
                      )}
                    </div>
                    {job.application_count > 0 && (
                      <div className="my-job-applications">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                          <circle cx="9" cy="7" r="4"></circle>
                          <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                          <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                        </svg>
                        <span>{job.application_count} {job.application_count === 1 ? 'application' : 'applications'}</span>
                      </div>
                    )}
                  </div>
                ))}
                
                {/* Load More Button - Mobile */}
                {hasMoreJobs && (
                  <div className="my-jobs-load-more">
                    <button
                      className="load-more-btn"
                      onClick={handleLoadMore}
                      disabled={loadingMore || loading}
                    >
                      {loadingMore ? (
                        <>
                          <div className="load-more-spinner"></div>
                          Loading...
                        </>
                      ) : (
                        'Load More'
                      )}
                    </button>
                    {totalJobs > 0 && (
                      <div className="load-more-info">
                        Showing {jobs.length} of {totalJobs} jobs
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      </div>

      <Footer />
      <BottomNavigationBar 
        onHomeClick={onHomeClick}
        onCategoriesClick={onCategoriesClick}
        onHowItWorksClick={onHowItWorksClick}
        onProfileClick={onProfileClick}
        onAccountSettingsClick={onAccountSettingsClick}
        onForumClick={onForumClick}
        onJobsClick={() => navigate('/vacancies')}
        isLoggedIn={isAuthenticated}
        user={user}
      />

      {showJobPostingForm && (
        <JobPostingForm
          isOpen={showJobPostingForm}
          onClose={handleFormClose}
          onSuccess={handleFormSuccess}
          jobToEdit={jobToEdit}
        />
      )}

      {/* Recruiter Terms Modal */}
      <RecruiterTermsModal
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
        onAccept={handleTermsAccept}
      />

      {/* Mobile Job Detail Modal */}
      {showMobileModal && selectedJob && isMobile && createPortal(
        <div className="job-detail-mobile-modal-overlay" onClick={(e) => {
          if (e.target === e.currentTarget) {
            setShowMobileModal(false);
          }
        }}>
          <div className="job-detail-mobile-modal" onClick={(e) => e.stopPropagation()}>
            <div className="job-detail-mobile-modal-header">
              <h2>Job Details</h2>
              <button 
                className="job-detail-mobile-modal-close"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowMobileModal(false);
                }}
                aria-label="Close"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            <div className="job-detail-mobile-modal-content">
              <JobDetailPanel 
                job={selectedJob} 
                applications={applications[selectedJob.id] || []}
                onJobUpdate={() => {
                  fetchMyJobs(true, true); // Reset and preserve selection if job still exists
                }}
                onEdit={handleEditJob}
              />
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default MyJobsPage;

